/**
 * Global image fade-in helper.
 * Pairs with the `img.is-loaded` CSS rule in index.css so every <img> in the
 * app fades in smoothly once it has decoded — including cached images and
 * images added later by React or third-party portals.
 *
 * Opt-out per image with `data-no-fade` or class `no-fade`.
 */
const LOADED = "is-loaded";

function mark(img: HTMLImageElement) {
  if (img.dataset.noFade !== undefined || img.classList.contains("no-fade")) return;
  if (img.classList.contains(LOADED)) return;
  // If the image is already fully loaded (cached) flip immediately.
  if (img.complete && img.naturalWidth > 0) {
    img.classList.add(LOADED);
    return;
  }
  const onLoad = () => {
    img.classList.add(LOADED);
    img.removeEventListener("load", onLoad);
    img.removeEventListener("error", onError);
  };
  const onError = () => {
    // Don't keep broken images invisible.
    img.classList.add(LOADED);
    img.removeEventListener("load", onLoad);
    img.removeEventListener("error", onError);
  };
  img.addEventListener("load", onLoad);
  img.addEventListener("error", onError);
}

export function initImageFadeIn() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  // Initial pass for already-rendered images.
  document.querySelectorAll("img").forEach((img) => mark(img as HTMLImageElement));

  // Watch for newly added <img> nodes anywhere in the tree (incl. Radix portals).
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.tagName === "IMG") {
          mark(node as HTMLImageElement);
        } else {
          node.querySelectorAll?.("img").forEach((img) => mark(img as HTMLImageElement));
        }
      });
      // Re-evaluate when src attribute changes (e.g. avatar swap).
      if (m.type === "attributes" && m.target instanceof HTMLImageElement && m.attributeName === "src") {
        m.target.classList.remove(LOADED);
        mark(m.target);
      }
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["src"],
  });
}